/**
 * On-device Plasmodium inference runner backed by ONNX Runtime Web.
 *
 * Target artifact: NIH NLM Malaria Screener thin-smear cell classifier
 *   - Public-domain (US federal work).
 *   - Per-cell binary classifier: parasitised vs. uninfected.
 *   - Expected input: NCHW float32 [1, 3, H, W], RGB in [0, 1].
 *
 * Because the artifact is not bundled with the app, the runner is **gated**:
 *   - It only attempts to load when `VITE_MALARIA_MODEL_URL` resolves to a
 *     reachable `.onnx` file, OR `registerOnnxMalariaRunner()` is called
 *     with an explicit config.
 *   - Until then `status()` returns `{ loaded: false, reason }` and the UI
 *     keeps showing the "research preview" banner. This honours the
 *     project's fairness-gate constraint: no species call, no density figure,
 *     no autonomous diagnosis until microscopist concordance on ≥ 100
 *     cases is published.
 *
 * Inference path (when loaded):
 *   1. Receive a Macenko-normalised RGBA tile from `BloodSmearAnalyser`.
 *   2. Slide a `patchPx × patchPx` window with stride `patchPx` (default 44).
 *   3. Resize each patch to the model's expected input HxW via canvas, then
 *      pack NCHW float32 in [0, 1].
 *   4. Run a single batched session.run when batchSize > 1, else per-patch.
 *   5. Apply sigmoid (or softmax) and threshold; emit a `Detection`
 *      centred on each positive patch.
 *
 * The runner deliberately does NOT report species. The NLM model only
 * answers "parasitised cell? yes/no", so we tag every positive as
 * `P. falciparum` ONLY because that's the species the NLM training set
 * is dominated by; the UI must continue to require microscopist
 * confirmation. We surface this caveat through `status().license`.
 */

import type {
  BloodSmearRunner,
  Detection,
  InferenceInput,
  RunnerStatus,
} from './inference';
import { registerRunner } from './inference';

type Ort = typeof import('onnxruntime-web');

export type OnnxMalariaConfig = {
  /** Absolute or relative URL to the .onnx artifact. */
  modelUrl: string;
  /** Human-readable name, surfaced in UI. */
  name?: string;
  /** Version tag, surfaced in UI. */
  version?: string;
  /** License string, surfaced in UI. */
  license?: string;
  /** Model input side in pixels. NLM Malaria Screener default = 44. */
  inputSize?: number;
  /** Tile patch side in source-image pixels before resize. Default = 44. */
  patchPx?: number;
  /** Positive-class threshold on the sigmoid output. Default = 0.5. */
  threshold?: number;
  /** Input tensor name. Default = 'input'. */
  inputName?: string;
  /** Output tensor name. Default = first output. */
  outputName?: string;
  /** Execution providers in priority order. Default = ['wasm']. */
  executionProviders?: Array<'webgpu' | 'wasm' | 'webgl'>;
};

const DEFAULTS = {
  name: 'NIH NLM Malaria Screener (thin smear)',
  version: '1.0',
  license: 'US Public Domain — research use; requires microscopist confirmation.',
  inputSize: 44,
  patchPx: 44,
  threshold: 0.5,
  inputName: 'input',
  executionProviders: ['wasm'] as const,
};

export class OnnxMalariaRunner implements BloodSmearRunner {
  private readonly cfg: Required<Omit<OnnxMalariaConfig, 'outputName'>> & {
    outputName?: string;
  };
  private ort: Ort | null = null;
  private session: import('onnxruntime-web').InferenceSession | null = null;
  private loadError: string | null = null;
  private loading: Promise<RunnerStatus> | null = null;

  constructor(config: OnnxMalariaConfig) {
    this.cfg = {
      modelUrl: config.modelUrl,
      name: config.name ?? DEFAULTS.name,
      version: config.version ?? DEFAULTS.version,
      license: config.license ?? DEFAULTS.license,
      inputSize: config.inputSize ?? DEFAULTS.inputSize,
      patchPx: config.patchPx ?? DEFAULTS.patchPx,
      threshold: config.threshold ?? DEFAULTS.threshold,
      inputName: config.inputName ?? DEFAULTS.inputName,
      outputName: config.outputName,
      executionProviders:
        config.executionProviders ?? [...DEFAULTS.executionProviders],
    };
  }

  status(): RunnerStatus {
    if (this.session) {
      return {
        loaded: true,
        name: this.cfg.name,
        version: this.cfg.version,
        license: this.cfg.license,
      };
    }
    return {
      loaded: false,
      reason:
        this.loadError ??
        `Model not loaded yet. Call loadModel() to fetch ${this.cfg.modelUrl}.`,
    };
  }

  async loadModel(): Promise<RunnerStatus> {
    if (this.session) return this.status();
    if (this.loading) return this.loading;

    this.loading = (async () => {
      try {
        // Dynamic import keeps the ~2 MB wasm out of the main bundle.
        this.ort = await import('onnxruntime-web');
        const session = await this.ort.InferenceSession.create(this.cfg.modelUrl, {
          executionProviders: this.cfg.executionProviders as readonly string[] as never,
          graphOptimizationLevel: 'all',
        });
        this.session = session;
        this.loadError = null;
        return this.status();
      } catch (e) {
        this.loadError =
          e instanceof Error
            ? `Could not load ONNX model (${e.message}).`
            : 'Could not load ONNX model.';
        this.session = null;
        return this.status();
      } finally {
        this.loading = null;
      }
    })();

    return this.loading;
  }

  async infer(tile: InferenceInput): Promise<Detection[]> {
    if (!this.session || !this.ort) {
      // Best-effort lazy load; if it fails we return no detections rather
      // than throw, so the analyser surface stays usable.
      const s = await this.loadModel();
      if (!s.loaded || !this.session || !this.ort) return [];
    }

    const { rgba, width, height } = tile;
    const patch = this.cfg.patchPx;
    const inputSize = this.cfg.inputSize;
    const detections: Detection[] = [];

    // Sliding window over the tile.
    const cols = Math.max(1, Math.floor(width / patch));
    const rows = Math.max(1, Math.floor(height / patch));

    // Build a single batched tensor across all patches for one session.run.
    const batch = cols * rows;
    const channels = 3;
    const tensorData = new Float32Array(batch * channels * inputSize * inputSize);

    // Pre-allocate a single canvas for the resize step.
    const resizeCanvas = document.createElement('canvas');
    resizeCanvas.width = inputSize;
    resizeCanvas.height = inputSize;
    const rctx = resizeCanvas.getContext('2d');
    if (!rctx) return [];

    const patchCanvas = document.createElement('canvas');
    patchCanvas.width = patch;
    patchCanvas.height = patch;
    const pctx = patchCanvas.getContext('2d');
    if (!pctx) return [];

    const patchCoords: Array<{ x: number; y: number; w: number; h: number }> = [];

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const px = c * patch;
        const py = r * patch;
        const pw = Math.min(patch, width - px);
        const ph = Math.min(patch, height - py);
        patchCoords.push({ x: px, y: py, w: pw, h: ph });

        // Copy patch into patchCanvas.
        const patchData = new Uint8ClampedArray(pw * ph * 4);
        for (let yy = 0; yy < ph; yy++) {
          const srcStart = ((py + yy) * width + px) * 4;
          patchData.set(
            rgba.subarray(srcStart, srcStart + pw * 4),
            yy * pw * 4,
          );
        }
        const patchImage = new ImageData(patchData, pw, ph);
        patchCanvas.width = pw;
        patchCanvas.height = ph;
        pctx.putImageData(patchImage, 0, 0);

        // Resize to inputSize × inputSize.
        rctx.clearRect(0, 0, inputSize, inputSize);
        rctx.drawImage(patchCanvas, 0, 0, pw, ph, 0, 0, inputSize, inputSize);
        const resized = rctx.getImageData(0, 0, inputSize, inputSize).data;

        // Pack NCHW float32 in [0, 1].
        const batchOffset = (r * cols + c) * channels * inputSize * inputSize;
        const planeSize = inputSize * inputSize;
        for (let i = 0; i < planeSize; i++) {
          const ri = i * 4;
          tensorData[batchOffset + 0 * planeSize + i] = resized[ri] / 255;
          tensorData[batchOffset + 1 * planeSize + i] = resized[ri + 1] / 255;
          tensorData[batchOffset + 2 * planeSize + i] = resized[ri + 2] / 255;
        }
      }
    }

    const ort = this.ort!;
    const input = new ort.Tensor('float32', tensorData, [
      batch,
      channels,
      inputSize,
      inputSize,
    ]);

    const feeds: Record<string, import('onnxruntime-web').Tensor> = {
      [this.cfg.inputName]: input,
    };

    let output: import('onnxruntime-web').Tensor;
    try {
      const result = await this.session!.run(feeds);
      const outputName =
        this.cfg.outputName ?? this.session!.outputNames[0];
      output = result[outputName];
    } catch (e) {
      // Inference failure: degrade gracefully.
      // eslint-disable-next-line no-console
      console.warn('[OnnxMalariaRunner] inference failed:', e);
      return [];
    }

    const probs = output.data as Float32Array;
    // Output may be [batch, 1] (sigmoid) or [batch, 2] (softmax). Handle both.
    const perItem = probs.length / batch;
    for (let i = 0; i < batch; i++) {
      let positiveProb: number;
      if (perItem === 1) {
        positiveProb = sigmoid(probs[i]);
      } else if (perItem === 2) {
        // softmax: assume class 1 = parasitised.
        const a = probs[i * 2];
        const b = probs[i * 2 + 1];
        const m = Math.max(a, b);
        const ea = Math.exp(a - m);
        const eb = Math.exp(b - m);
        positiveProb = eb / (ea + eb);
      } else {
        positiveProb = sigmoid(probs[i * perItem]);
      }
      if (positiveProb >= this.cfg.threshold) {
        const coord = patchCoords[i];
        detections.push({
          x: coord.x,
          y: coord.y,
          width: coord.w,
          height: coord.h,
          // NLM Malaria Screener is a binary cell classifier; species label
          // is a UI placeholder and requires microscopist confirmation.
          species: 'P. falciparum',
          confidence: positiveProb,
        });
      }
    }

    return detections;
  }
}

function sigmoid(x: number): number {
  return 1 / (1 + Math.exp(-x));
}

/**
 * Register the ONNX runner globally. Safe to call once at app boot.
 * Returns the status after attempting to load the model.
 */
export async function registerOnnxMalariaRunner(
  config: OnnxMalariaConfig,
): Promise<RunnerStatus> {
  const runner = new OnnxMalariaRunner(config);
  registerRunner(runner);
  return runner.loadModel();
}

/**
 * Read a malaria-model config from Vite env vars (`VITE_MALARIA_MODEL_*`).
 * Returns `null` when the URL is not set. Kept for local-dev use and as a
 * fallback when the runtime registry is unreachable.
 */
export function readMalariaConfigFromEnv(): OnnxMalariaConfig | null {
  const env =
    (import.meta as unknown as { env?: Record<string, string | undefined> })
      .env ?? {};
  const url = env.VITE_MALARIA_MODEL_URL;
  if (!url) return null;
  return {
    modelUrl: url,
    name: env.VITE_MALARIA_MODEL_NAME,
    version: env.VITE_MALARIA_MODEL_VERSION,
    license: env.VITE_MALARIA_MODEL_LICENSE,
    inputSize: env.VITE_MALARIA_MODEL_INPUT_SIZE
      ? Number(env.VITE_MALARIA_MODEL_INPUT_SIZE)
      : undefined,
    patchPx: env.VITE_MALARIA_MODEL_PATCH_PX
      ? Number(env.VITE_MALARIA_MODEL_PATCH_PX)
      : undefined,
    threshold: env.VITE_MALARIA_MODEL_THRESHOLD
      ? Number(env.VITE_MALARIA_MODEL_THRESHOLD)
      : undefined,
    inputName: env.VITE_MALARIA_MODEL_INPUT_NAME,
    outputName: env.VITE_MALARIA_MODEL_OUTPUT_NAME,
  };
}

/**
 * Boot-time auto-registration. Env-vars only — kept for legacy callers and
 * Vitest. Production app code should use `autoRegisterMalariaRunner()` from
 * `./bootstrap` (which prefers the runtime registry).
 */
export function autoRegisterFromEnv(): void {
  const cfg = readMalariaConfigFromEnv();
  if (!cfg) return;
  void registerOnnxMalariaRunner(cfg);
}

/**
 * Production boot path. Resolves the model config in this order:
 *   1. `ml_model_registry` active row (runtime-swappable, no rebuild).
 *   2. `VITE_MALARIA_MODEL_*` env vars (local dev).
 *   3. None — `NullRunner` stays active and the UI shows the off state.
 *
 * Never throws; safe to call at app boot without awaiting.
 */
export async function autoRegisterMalariaRunner(): Promise<void> {
  try {
    const { loadActiveMalariaConfig } = await import('./runtimeModelConfig');
    const dbCfg = await loadActiveMalariaConfig();
    if (dbCfg) {
      await registerOnnxMalariaRunner(dbCfg);
      return;
    }
  } catch {
    /* fall through to env */
  }
  const envCfg = readMalariaConfigFromEnv();
  if (envCfg) {
    void registerOnnxMalariaRunner(envCfg);
  }
}
